import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { ITenantDetails } from "../../@types/Login";
import { Burger } from "../../assets/icons/Burger";
import { Close } from "../../assets/icons/Close";
import { PMPLogo } from "../../assets/icons/PMPLogo";
import { AUTH_TENANT_DETAILS } from "../../constants/localStorageKeys";
import { safeStorage, safeJsonParse } from "../../utils/storage";
import {
  Container,
  LogoContainer,
  MobileMenuButton,
} from "./TopPanelMenu.styles";

export type TopPanelMenuComponentProps = {
  onLogoClick?: () => void;
  children?: React.ReactNode;
};

export type TopBarMobileMenuButtonComponentProps = {
  onClick: (isMenuOpen: boolean) => void;
  isMobileLeftPanelMenuOpen?: boolean;
  smallScreenWidth?: number;
};

function TopPanelMenuMobileButtonComponent({
  onClick,
  isMobileLeftPanelMenuOpen = false,
  smallScreenWidth = 768,
}: TopBarMobileMenuButtonComponentProps) {
  const [isSmallScreen, setIsSmallScreen] = useState(
    window.innerWidth <= smallScreenWidth
  );

  const handleOnClickOverButton = () => {
    onClick(isMobileLeftPanelMenuOpen);
  };

  useEffect(() => {
    const onResize = () => {
      setIsSmallScreen(window.innerWidth <= smallScreenWidth);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // Only display for small screens
  if (!isSmallScreen) {
    return <MobileMenuButton style={{ display: "none" }} />;
  }
  return (
    <MobileMenuButton onClick={handleOnClickOverButton}>
      {isMobileLeftPanelMenuOpen ? <Close /> : <Burger />}
    </MobileMenuButton>
  );
}

type LogoFormat = "svg" | "png" | "default";
type LogoFileFormat = Exclude<LogoFormat, "default">;

const LOGO_FORMAT_CACHE_KEY = "tenantLogoFormat";

// Appended to the logo URL once per page load. Logos are served from a fixed
// URL per tenant, and objects uploaded before the API started sending
// Cache-Control were cached heuristically for days, so a replaced logo kept
// showing until the browser felt like revalidating. A new query string per
// load costs one small request and makes every login see the current file.
const LOAD_STAMP = Date.now().toString(36);

function tenantLogoUrl(tenantId: string, format: LogoFileFormat): string {
  return `${process.env.REACT_APP_TENANT_LOGO_BASE_URL}${tenantId}.${format}?v=${LOAD_STAMP}`;
}

// The order the two logo files are tried in, starting from the last format
// that worked. Retrying the other file before giving up is what lets the
// header recover when a tenant's logo is replaced with the other type (the
// upload deletes the old file, so a cached "png" 404s after an SVG upload).
function probeOrder(start: LogoFileFormat): LogoFileFormat[] {
  return start === "svg" ? ["svg", "png"] : ["png", "svg"];
}

function getTenantId(): string | null {
  const json = safeStorage.getItem(AUTH_TENANT_DETAILS);
  if (!json) return null;
  const parsed = safeJsonParse<ITenantDetails | null>(json, null);
  return parsed?.tenantId ?? null;
}

// We cache the last-known-good logo format per tenant in localStorage so the
// next page load skips the svg → png → default fallback chain (each step waits
// on a 404 round-trip, which is what causes the 1-second flicker).
function readCachedLogoFormat(tenantId: string | null): LogoFormat {
  if (!tenantId) return "svg";
  const raw = safeStorage.getItem(`${LOGO_FORMAT_CACHE_KEY}:${tenantId}`);
  if (raw === "svg" || raw === "png" || raw === "default") return raw;
  return "svg";
}

function writeCachedLogoFormat(
  tenantId: string | null,
  format: LogoFormat
): void {
  if (!tenantId) return;
  safeStorage.setItem(`${LOGO_FORMAT_CACHE_KEY}:${tenantId}`, format);
}

// When the cache says a tenant has no logo, the default is shown at once (no
// flicker) and the two files are probed off-screen. Without this a tenant
// that later gets a logo kept the patientMpower one on every device until
// localStorage was cleared, because the <img> that discovers it was never
// rendered.
function probeForLogo(
  tenantId: string,
  onFound: (format: LogoFileFormat) => void
): () => void {
  let cancelled = false;
  const formats = probeOrder("svg");

  const tryNext = (index: number) => {
    if (cancelled || index >= formats.length) return;
    const format = formats[index];
    const image = new Image();
    image.onload = () => {
      if (!cancelled) onFound(format);
    };
    image.onerror = () => tryNext(index + 1);
    image.src = tenantLogoUrl(tenantId, format);
  };

  tryNext(0);
  return () => {
    cancelled = true;
  };
}

function TopPanelMenuComponent({
  children,
  onLogoClick,
}: TopPanelMenuComponentProps) {
  const { t } = useTranslation();

  const tenantId = getTenantId();
  const [logoState, setLogoState] = useState<LogoFormat>(() =>
    tenantId ? readCachedLogoFormat(tenantId) : "default"
  );
  // Which of the two files failed this session, so the error handler can
  // move to the other one before falling back to the default.
  const [failedFormats, setFailedFormats] = useState<LogoFileFormat[]>([]);

  useEffect(() => {
    if (!tenantId || logoState !== "default") return undefined;
    return probeForLogo(tenantId, (format) => {
      setLogoState(format);
      writeCachedLogoFormat(tenantId, format);
    });
  }, [tenantId, logoState]);

  const handleLogoError = () => {
    if (logoState === "default") return;
    const failed = [...failedFormats, logoState];
    const next =
      probeOrder(logoState).find((format) => !failed.includes(format)) ??
      "default";
    setFailedFormats(failed);
    setLogoState(next);
    writeCachedLogoFormat(tenantId, next);
  };

  const handleLogoLoad = () => {
    writeCachedLogoFormat(tenantId, logoState);
  };

  return (
    <Container>
      <LogoContainer
        role="button"
        onClick={onLogoClick}
        aria-label={t("topPanel_logoLabel")}
      >
        {tenantId && logoState !== "default" ? (
          <img
            src={tenantLogoUrl(tenantId, logoState)}
            alt={t("topPanel_logoAlt")}
            onError={handleLogoError}
            onLoad={handleLogoLoad}
          />
        ) : (
          <PMPLogo />
        )}
      </LogoContainer>

      {children}
    </Container>
  );
}

export { TopPanelMenuComponent, TopPanelMenuMobileButtonComponent };
